import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGame } from '../context/GameContext';
import { AvatarChip } from '../components/Avatar';
import { ArrowLeft, Trophy, Crown, Timer, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyBrawl() {
  const { setScreen, startWeeklyRun, playerName } = useGame();
  const [brawl, setBrawl] = useState(null);
  const [entries, setEntries] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wc, wl] = await Promise.all([
          axios.get(`${API}/weekly/current`),
          axios.get(`${API}/weekly/leaderboard`),
        ]);
        if (!cancelled) {
          setBrawl(wc.data);
          setEntries(wl.data?.entries || []);
        }
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const startBrawl = () => {
    if (!brawl) return;
    startWeeklyRun && startWeeklyRun(brawl.seed);
  };

  const copySeed = async () => {
    if (!brawl) return;
    try { await navigator.clipboard.writeText(brawl.seed); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch (e) {}
  };

  const daysLeft = brawl ? Math.max(0, Math.ceil((new Date(brawl.ends_at) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="min-h-screen w-full px-6 py-6" data-testid="weekly-brawl-screen">
      <div className="flex items-center justify-between mb-4">
        <button className="brutal-btn text-sm" onClick={() => setScreen('menu')} data-testid="weekly-back">
          <ArrowLeft size={16}/> BACK
        </button>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-[color:var(--pink)] flex items-center gap-3">
          <Trophy /> WEEKLY BRAWL
        </h1>
        <div />
      </div>

      {brawl && (
        <div className="max-w-3xl mx-auto brutal-border p-5 bg-[#0a0a0a]" style={{ borderColor: 'var(--pink)', boxShadow: '6px 6px 0 var(--pink)' }} data-testid="weekly-info">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--text-dim)]">WEEK {brawl.week_key}</div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <div className="text-xs uppercase text-[color:var(--text-dim)]">SEED</div>
            <div className="font-display font-black text-3xl text-[color:var(--pink)]" data-testid="weekly-seed">{brawl.seed}</div>
            <button className="brutal-btn text-xs px-3 py-1" onClick={copySeed} data-testid="weekly-copy-seed">
              {copied ? <><Check size={12}/> COPIED</> : <><Copy size={12}/> COPY</>}
            </button>
          </div>
          <div className="mt-3 text-xs text-[color:var(--text-dim)] flex items-center gap-2">
            <Timer size={14} className="text-[color:var(--neon)]"/>
            <span data-testid="weekly-days-left">{daysLeft} DAY{daysLeft !== 1 ? 'S' : ''} LEFT</span>
            <span className="ml-2">· CHAMP GETS PERMANENT TITLE</span>
          </div>
          <button className="mt-4 brutal-btn text-lg px-8 py-3 w-full flex items-center justify-center gap-2" onClick={startBrawl} data-testid="weekly-start" style={{ borderColor: 'var(--pink)', color: 'var(--pink)', boxShadow: '5px 5px 0 var(--pink)' }}>
            <Trophy size={18}/> ENTER THE BRAWL
          </button>
          <button className="mt-2 brutal-btn text-sm w-full flex items-center justify-center gap-2" onClick={() => setScreen('hall')} data-testid="weekly-hall-link" style={{ borderColor: '#ffd700', color: '#ffd700' }}>
            <Crown size={14}/> HALL OF CHAMPIONS
          </button>
        </div>
      )}

      {/* Weekly leaderboard */}
      <div className="mt-8 max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)] mb-2">THIS WEEK{'\u2019'}S TOP 25</div>
        <div className="space-y-1" data-testid="weekly-leaderboard-list">
          {entries.length === 0 && (
            <div className="text-center text-[color:var(--text-dim)] text-sm py-6" data-testid="weekly-empty">
              No runs submitted yet this week. Be the first champion.
            </div>
          )}
          {entries.map((e, i) => {
            const isMe = e.player_name === (playerName || '').toUpperCase();
            return (
              <div
                key={e.id || i}
                className="flex items-center gap-3 p-2 brutal-border-dim"
                data-testid={`weekly-row-${i}`}
                style={{ borderColor: i === 0 ? 'var(--pink)' : isMe ? 'var(--neon)' : '#333' }}
              >
                <div className="font-display font-black text-xl w-8 text-center" style={{ color: i === 0 ? 'var(--pink)' : 'var(--text-dim)' }}>
                  #{i + 1}
                </div>
                {i === 0 && <Crown size={16} className="text-[color:var(--pink)]"/>}
                {e.avatar && (e.avatar.emoji || e.avatar.drawing) && (
                  <AvatarChip avatar={e.avatar} size={28} testid={`weekly-avatar-${i}`} />
                )}
                <div className="flex-1 font-display font-black uppercase truncate">{e.player_name}</div>
                <div className="text-xs font-mono text-[color:var(--text-dim)]">F{e.floor_reached}</div>
                <div className="text-sm font-mono text-[color:var(--neon)]">{e.score}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
